import React, { useEffect, useState } from 'react'

import { SxProps, ThemeProvider } from '@mui/material'

import { WidgetType } from 'types/GlobalWidget'

import { useCustomization } from 'redux/hooks/Customization'
import { useMetric, useMetricRawData } from 'redux/hooks/Metrics'
import { useMetricActions } from 'redux/actions/Metrics'
import { usePrevious } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Skeleton from 'components_new/atoms/Skeleton'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import FitText from 'components_new/molecules/FitText'
import LoadingSection from 'components_new/molecules/LoadingSection'

import EditableText from 'components_new/organisms/Widget/WidgetHeader/EditableText'
import WidgetBase from 'components_new/organisms/Widget/WidgetBase'

import { formatValue } from 'components_new/organisms/Widget/Types/utils'
import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'
import { getTheme, getWidgetMode } from 'themes'
import { useTranslation } from 'language/translator'
import { TMetricSettings } from 'types/GlobalMetric'
import { getMetricTitle } from 'utils/metric'
import { TMetricDialogVariant } from '../lib'

export interface Props {
  id: string
  settings: TMetricSettings
  sx?: SxProps
  variant: TMetricDialogVariant
}

const PreviewDashboardWidget = (props: Props) => {
  const { id, settings, sx, variant } = props

  const { lang, translateEnum } = useTranslation()
  const { tryFetchMetricData } = useMetricActions()

  const metric = useMetric(id, settings.variant_kpi_template_id)

  const [rawData, rawDataError] = useMetricRawData({
    id,
    settings
  })
  const value = rawData?.key_figure?.value ?? null
  // a new widget starts on automatic formatting, so the preview does too
  const scale = getAutoScale([value], metric?.unit, metric?.unit_prefixes)

  useEffect(() => {
    tryFetchMetricData(id, settings)
  }, [id, settings])

  // resize
  const [triggerResize, setTriggerResize] = useState(false)

  const previousValue = usePrevious(value)

  useEffect(() => {
    if (previousValue && previousValue !== value) {
      setTriggerResize(true)
    }
  }, [value, previousValue])

  // customization
  const orgCustomization = useCustomization()
  const customization =
    variant === 'reports'
      ? { ...orgCustomization, background_colors: null, text_colors: null }
      : orgCustomization
  const widgetMode = getWidgetMode(customization)

  // loading
  const loading = !id || !metric || (!rawData && !rawDataError)

  return (
    <ThemeProvider theme={getTheme(widgetMode, customization)}>
      <Box sx={{ height: 256, position: 'relative', ...sx }}>
        <WidgetBase
          customization={customization}
          editMode={false}
          plain={false}
          scaleFactor={1}
          widgetType={WidgetType.KEY_FIGURE}
        >
          {rawDataError ? (
            <ErrorPlaceholder code={500} />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                position: 'relative',
                overflow: 'hidden',
                p: 2
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 'auto',
                  pb: 1
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                  <Box
                    sx={{
                      fontSize: 20,
                      flex: 1,
                      minWidth: 0,
                      width: '100%',
                      display: 'flex',
                      color: 'text.primary'
                    }}
                  >
                    {loading ? (
                      <Skeleton height={27} width={'50%'} />
                    ) : (
                      <EditableText
                        disabled={true}
                        onSubmit={() => {}}
                        scaleFactor={1}
                        value={getMetricTitle(
                          metric,
                          settings,
                          lang,
                          translateEnum
                        )}
                        variant="title"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  userSelect: 'none',
                  cursor: 'default'
                }}
              >
                {loading ? (
                  <LoadingSection />
                ) : (
                  <FitText
                    color="text.primary"
                    variant="h1"
                    triggerResize={triggerResize}
                    setTriggerResize={setTriggerResize}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'block'
                      }}
                    >
                      {formatValue(
                        value,
                        scale.unitPrefix,
                        0,
                        true,
                        undefined,
                        AUTO_SIGNIFICANT_FIGURES
                      )}
                      <Box component="span" sx={{ fontSize: '0.6em' }}>
                        {' '}
                        {scale.unitLabel}
                      </Box>
                    </Box>
                  </FitText>
                )}
              </Box>
            </Box>
          )}
        </WidgetBase>
      </Box>
    </ThemeProvider>
  )
}

export default PreviewDashboardWidget
